import {useState, useEffect, useCallback, useRef} from 'react'
import {useTranslation} from 'react-i18next'
import {useToast, PageTemplate, SectionCard} from '@livingdata/playground-core-front'
import {authClient, useSession} from '../auth-client'
import {primaryButtonClass, ErrorBanner} from './auth-layout'

// Settings inputs use a white fill (contrasting with the warm page) and stay
// flat — no shadow, unlike the elevated `fieldClass` used on the auth screens.
const settingsFieldClass
  = 'w-full rounded-md border border-border bg-card px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring'

// `primaryButtonClass` is full-width for the auth forms; settings actions sit
// inline below their section, so drop `w-full` (it would otherwise win the CSS
// cascade over an appended `w-auto`).
const settingsButtonClass = primaryButtonClass.replace('w-full', 'w-auto')

type Passkey = {
  id: string;
  name?: string | undefined;
  createdAt?: string | Date;
}

export function AccountPage() {
  const {t} = useTranslation()
  const {showToast} = useToast()
  const {data: session} = useSession()

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [savingProfile, setSavingProfile] = useState(false)

  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [changingPassword, setChangingPassword] = useState(false)
  const [passwordError, setPasswordError] = useState<string | undefined>()

  const [passkeys, setPasskeys] = useState<Passkey[]>([])
  const [newName, setNewName] = useState('')
  const [busy, setBusy] = useState(false)
  const [deletingId, setDeletingId] = useState<string | undefined>()

  // Prefill the profile fields once per user (firstName/lastName are inferred
  // additional fields; cast defensively so typing stays robust). Guarding on the
  // user id avoids clobbering in-progress edits when the session ref changes.
  const prefilledFor = useRef<string | undefined>(undefined)
  useEffect(() => {
    const user = session?.user as {id?: string; firstName?: string; lastName?: string} | undefined
    if (user?.id && prefilledFor.current !== user.id) {
      prefilledFor.current = user.id
      setFirstName(user.firstName ?? '')
      setLastName(user.lastName ?? '')
    }
  }, [session?.user])

  const handleSaveProfile = useCallback(async (event: React.SyntheticEvent) => {
    event.preventDefault()
    setSavingProfile(true)
    try {
      const name = `${firstName} ${lastName}`.trim()
      const {error} = await authClient.updateUser({firstName, lastName, name})
      if (error) {
        showToast(t('auth.account.profileError'), 'error')
        return
      }

      showToast(t('auth.account.profileSuccess'))
    } catch {
      showToast(t('auth.account.profileError'), 'error')
    } finally {
      setSavingProfile(false)
    }
  }, [firstName, lastName, showToast, t])

  const handleChangePassword = useCallback(async (event: React.SyntheticEvent) => {
    event.preventDefault()
    setPasswordError(undefined)

    if (newPassword !== confirmPassword) {
      setPasswordError(t('auth.account.passwordMismatch'))
      return
    }

    setChangingPassword(true)
    try {
      const {error} = await authClient.changePassword({
        currentPassword,
        newPassword,
        revokeOtherSessions: true
      })
      if (error) {
        showToast(t('auth.account.passwordError'), 'error')
        return
      }

      showToast(t('auth.account.passwordSuccess'))
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
    } catch {
      showToast(t('auth.account.passwordError'), 'error')
    } finally {
      setChangingPassword(false)
    }
  }, [currentPassword, newPassword, confirmPassword, showToast, t])

  const refresh = useCallback(async () => {
    const {data} = await authClient.passkey.listUserPasskeys()
    setPasskeys((data ?? []) as Passkey[])
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const handleAdd = useCallback(async (event: React.SyntheticEvent) => {
    event.preventDefault()
    setBusy(true)

    const {error} = await authClient.passkey.addPasskey({name: newName.trim() || undefined})

    setBusy(false)

    if (error) {
      showToast(t('auth.account.addError'), 'error')
      return
    }

    setNewName('')
    await refresh()
  }, [newName, refresh, showToast, t])

  const handleDelete = useCallback((id: string) => async () => {
    setDeletingId(id)
    try {
      const {error} = await authClient.passkey.deletePasskey({id})
      if (error) {
        showToast(t('auth.account.deleteError'), 'error')
        return
      }

      await refresh()
    } catch {
      showToast(t('auth.account.deleteError'), 'error')
    } finally {
      setDeletingId(undefined)
    }
  }, [refresh, showToast, t])

  return (
    <PageTemplate title={t('auth.account.title')} description={session?.user?.email} isHeaderTitleHidden>
      <div className='flex flex-col gap-8'>
        <SectionCard
          title={t('auth.account.profileTitle')}
          description={t('auth.account.profileDescription')}
          action={(
            <button type='submit' form='profile-form' disabled={savingProfile} className={settingsButtonClass}>
              {savingProfile ? t('auth.account.saving') : t('auth.account.save')}
            </button>
          )}
        >
          <form id='profile-form' onSubmit={handleSaveProfile} className='flex flex-col gap-4 sm:flex-row sm:gap-3'>
            <label className='flex flex-1 flex-col gap-1.5'>
              <span className='text-sm font-medium'>{t('auth.fields.firstName')}</span>
              <input
                type='text'
                value={firstName}
                required
                autoComplete='given-name'
                className={settingsFieldClass}
                onChange={e => {
                  setFirstName(e.target.value)
                }}
              />
            </label>
            <label className='flex flex-1 flex-col gap-1.5'>
              <span className='text-sm font-medium'>{t('auth.fields.lastName')}</span>
              <input
                type='text'
                value={lastName}
                required
                autoComplete='family-name'
                className={settingsFieldClass}
                onChange={e => {
                  setLastName(e.target.value)
                }}
              />
            </label>
          </form>
        </SectionCard>

        <SectionCard
          title={t('auth.account.passwordTitle')}
          description={t('auth.account.passwordDescription')}
          action={(
            <button type='submit' form='password-form' disabled={changingPassword} className={settingsButtonClass}>
              {changingPassword ? t('auth.account.changingPassword') : t('auth.account.changePassword')}
            </button>
          )}
        >
          <form id='password-form' onSubmit={handleChangePassword} className='flex flex-col gap-4'>
            {passwordError && <ErrorBanner message={passwordError} />}
            <label className='flex flex-col gap-1.5'>
              <span className='text-sm font-medium'>{t('auth.fields.password')}</span>
              <input
                type='password'
                value={currentPassword}
                required
                autoComplete='current-password'
                className={settingsFieldClass}
                onChange={e => {
                  setCurrentPassword(e.target.value)
                }}
              />
            </label>
            <label className='flex flex-col gap-1.5'>
              <span className='text-sm font-medium'>{t('auth.fields.newPassword')}</span>
              <input
                type='password'
                value={newPassword}
                required
                minLength={8}
                autoComplete='new-password'
                className={settingsFieldClass}
                onChange={e => {
                  setNewPassword(e.target.value)
                }}
              />
            </label>
            <label className='flex flex-col gap-1.5'>
              <span className='text-sm font-medium'>{t('auth.fields.confirmPassword')}</span>
              <input
                type='password'
                value={confirmPassword}
                required
                minLength={8}
                autoComplete='new-password'
                className={settingsFieldClass}
                onChange={e => {
                  setConfirmPassword(e.target.value)
                }}
              />
            </label>
          </form>
        </SectionCard>

        <SectionCard
          title={t('auth.account.passkeysTitle')}
          description={t('auth.account.passkeysDescription')}
          action={(
            <button type='submit' form='passkey-form' disabled={busy} className={settingsButtonClass + ' whitespace-nowrap'}>
              {busy ? t('auth.account.adding') : t('auth.account.addPasskey')}
            </button>
          )}
        >
          <div className='flex flex-col gap-4'>
            {passkeys.length === 0
              ? <p className='text-sm text-muted-foreground'>{t('auth.account.noPasskeys')}</p>
              : (
                <ul className='-my-1 divide-y divide-border'>
                  {passkeys.map(passkey => (
                    <li key={passkey.id} className='flex items-center justify-between py-2.5 text-sm'>
                      <span>{passkey.name || t('auth.account.defaultPasskeyName')}</span>
                      <button
                        type='button' disabled={deletingId === passkey.id}
                        className='text-destructive hover:underline disabled:opacity-60'
                        onClick={handleDelete(passkey.id)}
                      >
                        {t('auth.account.delete')}
                      </button>
                    </li>
                  ))}
                </ul>
              )}

            <form id='passkey-form' onSubmit={handleAdd}>
              <label className='flex flex-col gap-1.5'>
                <span className='text-sm font-medium'>{t('auth.account.namePlaceholder')}</span>
                <input
                  type='text' value={newName} className={settingsFieldClass}
                  onChange={e => {
                    setNewName(e.target.value)
                  }}
                />
              </label>
            </form>
          </div>
        </SectionCard>
      </div>
    </PageTemplate>
  )
}
